import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { Stack } from 'expo-router';

import { Colors } from '@/constants/theme';
import { useColorScheme } from '@/hooks/use-color-scheme';

/**
 * Disclaimers Screen
 * Displays important disclaimers and notices about using the FimFam-AI app.
 * Accessible from the settings screen.
 */
export default function DisclaimersScreen() {
  const colorScheme = useColorScheme();
  const backgroundColor = Colors[colorScheme ?? 'light'].background;
  const textColor = Colors[colorScheme ?? 'light'].text;
  const accentColor = Colors[colorScheme ?? 'light'].tint;

  return (
    <>
      <Stack.Screen
        options={{
          title: 'Disclaimers',
          headerShown: true,
        }}
      />
      <ScrollView
        style={[styles.container, { backgroundColor }]}
        contentContainerStyle={styles.content}>
        <View style={styles.section}>
          <Text style={[styles.heading, { color: accentColor }]}>AI Assistant Disclaimer</Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            FimFam-AI uses artificial intelligence to assist with calendar management and family
            coordination. While we strive for accuracy, AI can make mistakes. Always verify
            important information, dates, and suggested actions before confirming.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={[styles.heading, { color: accentColor }]}>Calendar Information</Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            This app accesses your device calendar with your permission. Events displayed are read
            from your selected calendars. We recommend reviewing all AI-generated calendar entries
            before saving them to your calendar.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={[styles.heading, { color: accentColor }]}>Data Accuracy</Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            Information provided by the AI assistant is for convenience purposes only. FimFam-AI
            does not guarantee the accuracy, completeness, or reliability of any information
            provided through the app.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={[styles.heading, { color: accentColor }]}>User Responsibility</Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            You are responsible for verifying all information and actions suggested by the AI
            assistant. Do not rely solely on AI-generated content for critical decisions or
            time-sensitive matters.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={[styles.heading, { color: accentColor }]}>Limitation of Liability</Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            FimFam-AI and its developers shall not be liable for any direct, indirect, incidental,
            or consequential damages resulting from the use or inability to use this application,
            including but not limited to missed appointments, scheduling conflicts, or data loss.
          </Text>
        </View>

        <View style={[styles.footer, { borderTopColor: '#e0e0e0' }]}>
          <Text style={[styles.footerText, { color: textColor }]}>
            Last Updated: May 2026
          </Text>
        </View>
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    paddingHorizontal: 20,
    paddingVertical: 24,
  },
  section: {
    marginBottom: 24,
  },
  heading: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 12,
  },
  paragraph: {
    fontSize: 14,
    lineHeight: 20,
    opacity: 0.85,
  },
  footer: {
    marginTop: 32,
    paddingTop: 20,
    borderTopWidth: 1,
  },
  footerText: {
    fontSize: 12,
    opacity: 0.5,
    textAlign: 'center',
  },
});
